import React, { useState, useEffect } from 'react';

const LocationPreview = ({ data }) => {
    const baseLocations = Array.isArray(data) ? data : [data];
    // Clone the first item to the end for seamless infinite looping
    const extendedLocations = baseLocations.length > 1
        ? [...baseLocations, baseLocations[0]]
        : baseLocations;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(true);

    useEffect(() => {
        if (baseLocations.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex(prev => prev + 1);
            setIsTransitioning(true);
        }, 3000);

        return () => clearInterval(interval);
    }, [baseLocations.length]);

    const handleTransitionEnd = () => {
        if (currentIndex === baseLocations.length) {
            setIsTransitioning(false);
            setCurrentIndex(0);
        }
    };

    return (
        <div className="w-full h-full relative bg-white overflow-hidden rounded-2xl group-container">
            {/* Slider Wrapper */}
            <div
                className={`flex h-full w-full ${isTransitioning ? 'transition-transform duration-[1500ms] ease-[cubic-bezier(0.25,1,0.5,1)]' : 'duration-0'}`}
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                onTransitionEnd={handleTransitionEnd}
            >
                {extendedLocations.map((loc, i) => (
                    <div
                        key={i}
                        className="min-w-full w-full h-full flex flex-col flex-shrink-0 bg-white"
                    >
                        {/* Image Section (Top 35%) */}
                        <div className="h-[35%] w-full relative overflow-hidden">
                            <img
                                src={loc.image}
                                alt={loc.title}
                                className="w-full h-full object-cover transform transition-transform duration-[3s] group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-amber-900/10 group-hover:bg-transparent transition-colors duration-500" />
                        </div>

                        {/* Content Section (Bottom 65%) with Pattern */}
                        <div className="h-[65%] w-full flex flex-col items-center text-center p-6 bg-white relative">
                            {/* Subtle Pattern Overlay */}
                            <div className="absolute inset-0 opacity-[0.03] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none" />

                            <div className="relative z-10 flex flex-col items-center h-full">
                                <h3 className="text-3xl font-serif text-amber-800 mb-3 italic">
                                    {loc.title}
                                </h3>

                                <div className="w-12 h-[1px] bg-amber-800/30 mb-4"></div>

                                <p className="text-sm text-slate-600 font-serif italic leading-relaxed mb-auto px-1 line-clamp-3">
                                    {loc.text}
                                </p>

                                {/* Map Container (Bottom) */}
                                <div className="w-full h-28 mt-4 rounded-lg overflow-hidden border border-amber-800/10 shadow-inner bg-slate-100">
                                    <iframe
                                        src={loc.mapUrl}
                                        className="w-full h-full border-0 grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
                                        loading="lazy"
                                        title={`Map ${loc.title}`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const LocationOverlay = ({ data }) => (
    <div className="flex flex-col h-full text-center md:text-left">
        <span className="text-lime-600 font-bold tracking-widest uppercase text-[10px] mb-3 block">Dettagli</span>
        <h3 className="text-3xl font-serif text-slate-800 mb-4">{data.overlayTitle}</h3>
        <p className="text-slate-600 text-sm leading-relaxed font-light mb-8">
            {data.description}
        </p>

        <ul className="space-y-3 mb-8 text-left bg-slate-50 p-6 rounded-xl border border-slate-100">
            {data.bullets && data.bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-3">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-lime-500 shrink-0" />
                    <span className="text-slate-700 text-sm font-medium">{bullet}</span>
                </li>
            ))}
        </ul>

        {data.techNote && (
            <div className="mt-auto pt-4 border-t border-slate-100">
                <code className="text-[10px] font-mono text-slate-400 block mb-1">TECH STACK</code>
                <code className="text-xs font-mono text-lime-700">{data.techNote}</code>
            </div>
        )}
    </div>
);

export default LocationPreview;
